import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getAuthToken } from '../lib/authToken';

interface TenantSettings {
  branding: {
    logo: string | null;
    primaryColor: string;
    secondaryColor: string;
    companyName: string;
    website: string;
  };
  features: {
    documentManagement: boolean;
    caseManagement: boolean;
    clientPortal: boolean;
    billing: boolean;
    reporting: boolean;
    emailIntegration: boolean;
    calendarIntegration: boolean;
    paymentGateway: boolean;
    eSignature: boolean;
    advancedAnalytics: boolean;
    apiAccess: boolean;
    whiteLabel?: boolean;
    sso?: boolean;
  };
  limits: {
    users: number;
    cases: number;
    clients: number;
    documents: number;
    storageGB: number;
    apiCalls: number;
  };
  notifications: {
    email: boolean;
    sms: boolean;
    push: boolean;
    webhook: boolean;
  };
  security: {
    twoFactorAuth: boolean;
    sessionTimeout: number;
    passwordPolicy: string;
    ipWhitelist: string[];
    /** Outbound document download/send guard (Wave P.5). Persisted via tenant.settings.security — no dedicated Settings UI yet. */
    requireAttorneyReviewBeforeOutbound?: boolean;
  };
  integrations: {
    googleCalendar: boolean;
    outlookCalendar: boolean;
    stripe: boolean;
    paypal: boolean;
    docusign: boolean;
    zoom: boolean;
  };
}

interface TenantMetadata {
  industry: string;
  size: string;
  location: string;
  timezone: string;
  language: string;
  currency: string;
  dateFormat: string;
  timeFormat: string;
}

interface TenantContactInfo {
  primaryContact: {
    name: string;
    email: string;
    phone: string;
    role: string;
  };
  billingContact: {
    name: string;
    email: string;
    phone: string;
  };
  technicalContact: {
    name: string;
    email: string;
    phone: string;
  };
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
}

interface TenantSubscription {
  startDate: string | null;
  endDate: string | null;
  trialEndDate: string | null;
  billingCycle: string;
  amount: number;
  currency: string;
  status: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
}

interface TenantUsage {
  users: number;
  cases: number;
  clients: number;
  documents: number;
  storageUsed: number;
  apiCalls: number;
  lastActivity: string | null;
}

interface Tenant {
  id: string;
  tenantNumber: string;
  name: string;
  subdomain: string;
  domain: string | null;
  status: 'active' | 'inactive' | 'suspended' | 'pending' | 'demo';
  type: 'demo' | 'trial' | 'paid' | 'enterprise';
  plan: 'basic' | 'professional' | 'enterprise' | 'custom';
  settings: TenantSettings;
  metadata: TenantMetadata;
  contactInfo: TenantContactInfo;
  subscription: TenantSubscription;
  usage: TenantUsage;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface TenantContextType {
  tenant: Tenant | null;
  isLoading: boolean;
  error: string | null;
  refreshTenant: () => Promise<void>;
  updateTenantSettings: (settings: Partial<TenantSettings>) => Promise<void>;
  getFeatureStatus: (feature: keyof TenantSettings['features']) => boolean;
  getUsagePercentage: (resource: keyof TenantUsage) => number;
  isWithinLimits: (resource: keyof TenantUsage, count?: number) => boolean;
  getBranding: () => TenantSettings['branding'];
  getLimits: () => TenantSettings['limits'];
  getFeatures: () => TenantSettings['features'];
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { isAuthenticated, token, user } = useAuth();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTenantInfo = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const token = getAuthToken();

      // Fetch tenant info and usage data in parallel
      const [tenantResponse, usageResponse] = await Promise.all([
        fetch('/api/tenant/info', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }),
        fetch('/api/tenant/usage', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        })
      ]);

      if (tenantResponse.status === 429 || usageResponse.status === 429) {
        console.warn('Rate limited on tenant info fetch');
        setError('Rate limited - please try again later');
        return;
      }

      if (!tenantResponse.ok) {
        const errorText = await tenantResponse.text();
        throw new Error('Failed to fetch tenant information');
      }

      if (!usageResponse.ok) {
        const errorText = await usageResponse.text();
        throw new Error('Failed to fetch usage information');
      }

      const tenantData = await tenantResponse.json();
      const usageData = await usageResponse.json();
      
      if (tenantData.success && usageData.success) {
        // Merge tenant info with real usage data
        const tenantWithUsage = {
          ...tenantData.data,
          usage: usageData.data.usage,
        };
        setTenant(tenantWithUsage);
      } else {
        throw new Error(tenantData.message || usageData.message || 'Failed to fetch tenant information');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching tenant info:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshTenant = async (): Promise<void> => {
    await fetchTenantInfo();
  };

  const updateTenantSettings = async (settings: Partial<TenantSettings>): Promise<void> => {
    try {
      const response = await fetch('/api/tenant/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({ settings }),
      });

      if (!response.ok) {
        throw new Error('Failed to update tenant settings');
      }

      const data = await response.json();
      
      if (data.success) {
        setTenant(prev => prev ? { ...prev, settings: { ...prev.settings, ...settings } } : null);
      } else {
        throw new Error(data.message || 'Failed to update tenant settings');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      throw err;
    }
  };

  const getFeatureStatus = (feature: keyof TenantSettings['features']): boolean => {
    return tenant?.settings?.features?.[feature] || false;
  };

  const getUsagePercentage = (resource: keyof TenantUsage): number => {
    if (!tenant) return 0;
    
    const usage = Number(tenant.usage[resource]) || 0;
    const limit = Number(tenant.settings?.limits?.[resource as keyof TenantSettings['limits']]) || 0;
    
    if (limit === 0) return 0;
    return Math.min((usage / limit) * 100, 100);
  };

  const isWithinLimits = (resource: keyof TenantUsage, count: number = 1): boolean => {
    if (!tenant) return false;
    
    const currentUsage = Number(tenant.usage[resource]) || 0;
    const limit = Number(tenant.settings?.limits?.[resource as keyof TenantSettings['limits']]) || 0;
    
    return (currentUsage + count) <= limit;
  };

  const getBranding = (): TenantSettings['branding'] => {
    return tenant?.settings?.branding || {
      logo: null,
      primaryColor: '#1976d2',
      secondaryColor: '#dc004e',
      companyName: '',
      website: '',
    };
  };

  const getLimits = (): TenantSettings['limits'] => {
    return tenant?.settings?.limits || {
      users: 5,
      cases: 50,
      clients: 100,
      documents: 1000,
      storageGB: 10,
      apiCalls: 1000,
    };
  };

  const getFeatures = (): TenantSettings['features'] => {
    return tenant?.settings?.features || {
      documentManagement: true,
      caseManagement: true,
      clientPortal: true,
      billing: true,
      reporting: true,
      emailIntegration: true,
      calendarIntegration: true,
      paymentGateway: false,
      eSignature: false,
      advancedAnalytics: false,
      apiAccess: false,
    };
  };

  // Refetch (or clear) tenant branding/settings whenever the authenticated
  // identity changes. Previously this only ran once on mount, so logout→login
  // as another firm in the same SPA tab could keep showing the prior tenant.
  useEffect(() => {
    if (!isAuthenticated || !token) {
      setTenant(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      fetchTenantInfo();
    }, 100);

    return () => clearTimeout(timer);
    // user.tenantId: refetch when switching tenants without a full reload
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token, user?.tenantId]);

  const value: TenantContextType = {
    tenant,
    isLoading,
    error,
    refreshTenant,
    updateTenantSettings,
    getFeatureStatus,
    getUsagePercentage,
    isWithinLimits,
    getBranding,
    getLimits,
    getFeatures,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}; 